/**
 * One piece: the top bar, the paper page (photo, headline, the article edited in place) and the
 * side panel. Every save on the page goes through one autosave (piece/autosave.ts) so each PATCH
 * carries the newest updated_at; the publish dialog is piece/PublishDialog.tsx.
 */
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type DraftStatus } from '../api/client'
import { describeError } from '../lib/describeError'
import { confirmClaim, generateSummary, getPiece, markPieceOpened, markSourceReviewed, patchPiece, putContentsLine, removeClaim, restartEdit, sourceChanges, type ChecklistItem, type PieceDetail } from './api'
import { actionHref, importHref, pieceHref } from './links'
import type { Destination } from './labels'
import { useEditorialTitle } from './titleContext'
import { adoptDetail, keepNewest, pieceFields } from './piece/adopt'
import { EditorBoundary } from './piece/EditorBoundary'
import { FramingDialog, type FramingTarget } from './piece/FramingDialog'
import { Headline } from './piece/Headline'
import type { DocumentEditorHandle } from './piece/editorTypes'
import { PhotoBlock } from './piece/PhotoBlock'
import { PhotoHelpBox } from './piece/PhotoHelpBox'
import { usePhotoHelpOn } from './usePhotoHelpOn'
import { useSectionBrandsOn } from './useSectionBrandsOn'
import { closingLine } from './piece/brandPicker'
import { BUSY_WRITE, SAVE_FIRST, isRevisingPiece, leadingSubtitle, pieceLock, pieceMode, RECHECK_NOTE, REVISE_NOTE, writableFields, writerLockReason } from './piece/pieceHelpers'
import { SidePanel, type SideTab } from './piece/SidePanel'
import { SummaryEditor } from './piece/SummaryEditor'
import { TopBar } from './piece/TopBar'
import { canPublish, useEditorialMe } from './me'
import { pieceKey, usePieceAutosave, type LocalField, type LocalFields } from './piece/usePieceAutosave'
import { WebChecks } from './piece/WebChecks'
import { LiveChanges } from './piece/LiveChanges'
import { changeSummary, changeSummaryText } from './piece/sourceChanges'
import { twinNotice } from './piece/saveInPlace'
import { btn, btnPrimary, btnSmall, focus, link } from './piece/ui'

// TipTap and ProseMirror are the bulk of the editor's weight: only the piece page loads them.
const DocumentEditor = lazy(() => import('./piece/DocumentEditor').then((m) => ({ default: m.DocumentEditor })))

export function PiecePage() {
  const { id } = useParams()
  const n = Number(id)
  if (!Number.isInteger(n) || n <= 0) return <p role="alert" className="p-8 font-ui text-[14px] text-radar-red">No such piece.</p>
  // Keyed so a different piece starts with clean save state and unsent edits.
  return <Piece key={n} id={n} />
}

function Piece({ id }: { id: number }) {
  const { publicationSlug } = useEditorialTitle()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: pieceKey(id),
    queryFn: () => getPiece(id),
    // An older copy (a refetch that started before a save) never replaces a newer one.
    structuralSharing: keepNewest,
    // A failed refresh keeps the page on the copy it has and tries again.
    refetchInterval: (query) => (query.state.status === 'error' && query.state.data ? 15_000 : false),
  })
  // Opening the piece takes it off the Desk's "Ready to review" card. Fire and forget: a
  // failure only leaves the card up a little longer.
  useEffect(() => {
    markPieceOpened(id).then(() => qc.invalidateQueries({ queryKey: ['editorial-desk', publicationSlug] }), () => {})
  }, [id, publicationSlug, qc])
  const status = useQuery({
    queryKey: ['section-draft-status', publicationSlug],
    queryFn: () => api.get<DraftStatus>(`/api/lab/section-drafts/draft-now/status?publication_slug=${publicationSlug}`),
    refetchInterval: (query) => (query.state.data?.running ? 3_000 : 15_000),
  })
  // A run that finishes (a fact recheck above all) has changed the piece: read it again.
  const wasRunning = useRef(false)
  const running = !!status.data?.running
  useEffect(() => {
    if (wasRunning.current && !running) qc.invalidateQueries({ queryKey: pieceKey(id) })
    wasRunning.current = running
  }, [running, id, qc])

  // Render from the cached copy whenever there is one: a failed refresh must not unmount the editor.
  if (q.data === undefined) {
    if (q.isError) return <p role="alert" className="p-8 font-ui text-[14px] text-radar-red">Could not load the piece. {describeError(q.error)}</p>
    return <p role="status" className="p-8 font-ui text-[14px] text-paper-dim">Loading the piece…</p>
  }
  return <LoadedPiece detail={q.data} status={status.data} running={running} refreshFailed={q.isError} />
}

function LoadedPiece({ detail: d, status, running, refreshFailed }: {
  detail: PieceDetail; status: DraftStatus | undefined; running: boolean; refreshFailed: boolean
}) {
  const { section, publicationSlug } = useEditorialTitle()
  const qc = useQueryClient()
  const me = useEditorialMe()
  const id = d.id
  const editorRef = useRef<DocumentEditorHandle>(null)
  const photoHelpOn = usePhotoHelpOn(publicationSlug)
  const brandsOn = useSectionBrandsOn(publicationSlug)
  /** A save took a typed closing line out of the body: say so until dismissed. */
  const [lineNotice, setLineNotice] = useState<string | null>(null)
  /** The photo the framing dialog is open on, if any. */
  const [framing, setFraming] = useState<FramingTarget | null>(null)
  const { autosave, snapshot, local, set, setBody, forget, reset } = usePieceAutosave(d)
  // the opening subtitle of the text as it stands in the editor, for the byline box's note
  const [subtitle, setSubtitle] = useState(() => leadingSubtitle(d.body_html))
  useEffect(() => { setSubtitle(leadingSubtitle(d.body_html)) }, [d.body_html])
  // A stale refusal recovered from (autosave.ts): put a body the server changed into the editor
  // when nothing of ours was waiting on it.
  useEffect(() => autosave.onRecovered((fresh, previous) => {
    const body = (fresh as PieceDetail).body_html ?? ''
    if (body !== (previous.body_html ?? '') && !autosave.hasUnsent('body_html')) editorRef.current?.setBody(body)
  }), [autosave])
  const [view, setView] = useState<'members' | 'public'>('members')
  const [showChanges, setShowChanges] = useState(false)
  const reviewed = useMutation({
    mutationFn: () => markSourceReviewed(d.id),
    onSuccess: (nd) => { qc.setQueryData(pieceKey(d.id), nd); setShowChanges(false) },
  })
  const [claimBusy, setClaimBusy] = useState(false)
  const [reloadError, setReloadError] = useState<string | null>(null)
  /** A live piece's summary as typed, before Save summary (null: the saved one). */
  const [summaryDraft, setSummaryDraft] = useState<string | null>(null)
  /** The saved summary that draft started from: Save refuses when someone changed it since. */
  const [summaryBase, setSummaryBase] = useState<string | null>(null)
  const onSummaryDraft = (v: string | null) => {
    if (v === null) setSummaryBase(null)
    else if (summaryDraft === null) setSummaryBase(d.public_summary ?? '')
    setSummaryDraft(v)
  }

  // When a Revise of this piece ends: go to the new version, or say why it failed.
  const navigate = useNavigate()
  const revisingNow = isRevisingPiece(status, id)
  // Latched while the run is seen (set during render, the supported pattern for state from props).
  const [sawRevise, setSawRevise] = useState(false)
  if (revisingNow && !sawRevise) setSawRevise(true)
  const reviseDone = sawRevise && !revisingNow && !!status && !status.running
  const reviseFailed = reviseDone && d.superseded_by_id == null && status?.last?.ok === false
    ? (status.last.error ?? 'No reason was recorded.') : null
  useEffect(() => {
    if (reviseDone && d.superseded_by_id != null) navigate(pieceHref(section.path, d.superseded_by_id))
  }, [reviseDone, d.superseded_by_id, navigate, section.path])

  const mode = pieceMode(d.status)
  // An edit draft (Edit on Live): saved over its live article, open only while it is a draft and
  // the article hasn't changed since it opened (edit_stale; the server refuses every write then).
  const isEdit = d.edits_article_id != null
  const editOpen = isEdit && ['draft', 'needs_attention'].includes(d.status) && !d.edit_stale
  // While a Revise or "Check facts again" runs on this piece it holds the row: the page waits
  // read-only (body, headline, fields, Save, Publish) until it ends.
  const lock = pieceLock(status, id)
  const revising = lock === 'revise'
  const checking = lock !== null
  // A writer cannot change a scheduled, live or taken-down piece (the server refuses it too).
  const isWriter = me.data !== undefined && !canPublish(me.data)
  const writerLock = writerLockReason(d.status, isWriter)
  const editable = mode === 'editable' && !checking && !writerLock && (!isEdit || editOpen)
  const savedDest: Destination = d.destination ?? d.brief?.destination ?? 'section'
  const dest: Destination = local.destination ?? savedDest
  const writable = checking || writerLock || (isEdit && !editOpen) ? 'none' : writableFields(d.status, savedDest)
  const isBoth = dest === 'both'
  const shown = isBoth ? view : 'members'

  /** Every field edit on the page, refused here when the piece cannot take it. */
  const setField = useCallback(<F extends LocalField>(f: F, v: NonNullable<LocalFields[F]>, opts?: { immediate?: boolean }) => {
    if (writable === 'all' || (writable === 'summary' && f === 'public_summary')) set(f, v, opts)
  }, [writable, set])

  /** Take a copy of the piece the autosave did not save (see adoptDetail): move the base and
   *  bring the editor in step, or go stale when a field with unsent edits changed on the server. */
  const adopt = useCallback((r: PieceDetail) => {
    const incoming = pieceFields(r)
    const action = adoptDetail({ base: autosave.base, held: autosave.held, unsent: autosave.unsentFields(), values: autosave.unsentValues() },
      { updated_at: r.updated_at, ...incoming })
    if (action.kind === 'ignore') return
    if (action.kind === 'stale') { autosave.markStale(); return }
    autosave.rebase(r.updated_at, incoming)
    if (action.setBody) editorRef.current?.setBody(r.body_html ?? '')
  }, [autosave])

  // Every copy the cache receives (a refetch after a recheck, a photo or a social change).
  useEffect(() => { adopt(d) }, [d, adopt])
  // A save whose body lost a typed closing line: say so (set during render, as for sawRevise
  // above), and show the server's body unless more was typed since.
  const [noticeSeen, setNoticeSeen] = useState<PieceDetail>(d)
  if (noticeSeen !== d) {
    setNoticeSeen(d)
    if (d.closing_line_notice) setLineNotice(d.closing_line_notice)
  }
  useEffect(() => {
    if (d.closing_line_notice && !autosave.hasUnsent('body_html')) editorRef.current?.setBody(d.body_html ?? '')
  }, [d, autosave])

  const refetch = useCallback(() => { qc.invalidateQueries({ queryKey: pieceKey(id) }) }, [qc, id])
  const statusChanged = () => qc.invalidateQueries({ queryKey: ['section-draft-status', publicationSlug] })

  /** A copy some other call returned (a claim, the summary, a photo, set aside): adopt it, then
   *  into the cache once any refetch in flight is cancelled. */
  const took = useCallback(async (r: PieceDetail) => {
    adopt(r)
    await qc.cancelQueries({ queryKey: pieceKey(id), exact: true })
    qc.setQueryData(pieceKey(id), r)
  }, [adopt, qc, id])
  /** Before a write outside the autosave: save what waits. A busy row refuses at once. */
  const beforeWrite = useCallback(async () => {
    const ok = await autosave.flush()
    if (!ok && autosave.state === 'busy') throw new Error(BUSY_WRITE)
    return ok
  }, [autosave])

  // Closing the tab or reloading with work outstanding: ask the browser to warn. Hiding the tab
  // sends what waits now rather than after the debounce.
  // A live summary typed but not saved counts too.
  const summaryUnsaved = summaryDraft !== null && summaryDraft !== (d.public_summary ?? '')
  useEffect(() => {
    const onLeave = (e: BeforeUnloadEvent) => {
      if (autosave.getSnapshot().state === 'saved' && !summaryUnsaved) return
      e.preventDefault()
      e.returnValue = ''
    }
    const onHide = () => { if (document.visibilityState === 'hidden') void autosave.flush() }
    window.addEventListener('beforeunload', onLeave)
    document.addEventListener('visibilitychange', onHide)
    return () => {
      window.removeEventListener('beforeunload', onLeave)
      document.removeEventListener('visibilitychange', onHide)
    }
  }, [autosave, summaryUnsaved])

  const reload = async () => {
    setReloadError(null)
    try {
      // Its own request, not the query's: a save landing meanwhile cancels query fetches.
      const fresh = await getPiece(id)
      reset(fresh)
      onSummaryDraft(null)
      editorRef.current?.setBody(fresh.body_html ?? '')
      await qc.cancelQueries({ queryKey: pieceKey(id), exact: true })
      qc.setQueryData(pieceKey(id), fresh)
    } catch (e) {
      setReloadError(`Could not reload the piece. ${describeError(e)}`)
    }
  }

  /** The brand picker: a pick saves at once, outside the autosave, once what waits has saved. */
  const onBrand = async (brandId: number | null) => {
    if (writable !== 'all') throw new Error('This piece cannot change.')
    if (!(await beforeWrite())) throw new Error(SAVE_FIRST)
    await took(await patchPiece(id, { brand_id: brandId, ...(autosave.base ? { base_updated_at: autosave.base } : {}) }))
  }

  /** The Contents line picker: saves at once, outside the autosave, once what waits has saved. */
  const onContentsLine = async (tocId: number | null) => {
    if (!(await beforeWrite())) throw new Error(SAVE_FIRST)
    await took(await putContentsLine(id, tocId))
  }

  const onConfirmClaim = async (text: string) => {
    await autosave.flush()
    await took(await confirmClaim(id, text))
  }
  const onRemoveClaim = async (text: string) => {
    await autosave.flush()
    const s = autosave.getSnapshot()
    if (s.state === 'busy') throw new Error(BUSY_WRITE)
    if (s.state === 'stale' || s.errors.body_html) throw new Error('Your last edits to the text have not saved, so the sentence cannot be removed yet.')
    setClaimBusy(true)
    try {
      // Nothing of the body is unsent now, so adopting the reply puts its text into the editor.
      await took(await removeClaim(id, text))
    } finally {
      setClaimBusy(false)
    }
  }
  const onWriteSummary = async () => {
    await autosave.flush()
    const r = await generateSummary(id)
    // Nothing typed or refused earlier may be resent over the summary just written.
    forget('public_summary')
    onSummaryDraft(null)
    await took(r)
  }
  /** A live piece's summary: saved only when asked, once, since it changes the public page at once. */
  const onSaveSummary = async (v: string): Promise<string | null> => {
    if (writable === 'none') throw new Error('This piece cannot change.')
    // Someone saved a different summary since this draft began: never write over it unseen.
    if (summaryBase !== null && (d.public_summary ?? '') !== summaryBase) {
      autosave.markStale()
      throw new Error('Someone else changed the summary. Reload it to see their version.')
    }
    const r = await autosave.saveNow({ public_summary: v })
    if (!r.ok) throw new Error(r.kind === 'busy' ? BUSY_WRITE : r.message)
    return (r.result as PieceDetail | undefined)?.note ?? null
  }
  const showClaim = (text: string) => {
    setView('members')
    requestAnimationFrame(() => editorRef.current?.focusClaim(text))
  }

  const [openTab, setOpenTab] = useState<{ tab: SideTab; n: number } | undefined>()
  const showItem = (item: ChecklistItem) => {
    if (item.claim) showClaim(item.claim)
    else setOpenTab((o) => ({ tab: 'search', n: (o?.n ?? 0) + 1 }))
  }

  const claims = editable
    ? (d.checklist ?? []).filter((i) => i.kind === 'unchecked' && i.claim).map((i) => i.claim as string)
    : []
  const sourcesLink = actionHref(section.path, 'check_sources', d.id)
  const tryAgainLink = actionHref(section.path, 'try_again', d.id)
  // Changing a live piece's free summary is publishing: an editor's job, not a writer's.
  const summaryEditable = writable !== 'none' && savedDest === 'both'
    && (canPublish(me.data) || !['published', 'unpublished'].includes(d.status))

  return (
    <div className="min-h-screen bg-ink-900 font-ui text-[14px] text-paper">
      <TopBar detail={d} sectionLabel={section.label} path={section.path} publicationSlug={publicationSlug}
              saveState={snapshot.state} saveReason={snapshot.reason} onReload={() => void reload()} running={running}
              onRetry={() => autosave.retry(snapshot.errorField ?? undefined)} beforeWrite={beforeWrite} locked={checking} onDetail={(r) => void took(r)}
              destination={dest} onDestination={(v) => setField('destination', v, { immediate: true })}
              onChanged={() => { refetch(); statusChanged() }} onReviseStarted={statusChanged} onShowItem={showItem} />
      <div className="mx-auto flex max-w-[1360px] flex-wrap items-start gap-6 px-4 pb-16 pt-6 md:px-8">
        <div className="flex min-w-0 flex-[999_1_600px] flex-col gap-3.5">
          {snapshot.state === 'stale' && (
            <Banner tone="bad" text="Someone else changed this piece. Reload it to see their version. Your unsaved changes here will be lost.">
              <button type="button" className={btnPrimary} onClick={() => void reload()}>Reload it</button>
            </Banner>
          )}
          {(d.import_info || d.contents_line) && (
            <div role="status" className="flex flex-col gap-1.5 rounded-xl border border-ink-500 bg-ink-700 px-[18px] py-4 font-ui">
              {d.import_info && (
                <p className="m-0 text-[15px] font-semibold text-paper">
                  <Link to={importHref(section.path, d.import_info.id)} className={link}>
                    From import: {d.import_info.file_names.join(', ')}
                  </Link>
                  {' · '}uploaded by {d.import_info.created_by} on {importDate(d.import_info.created_at)}
                </p>
              )}
              {d.contents_line && (
                <ContentsLinePicker line={d.contents_line} disabled={checking || !!writerLock || isEdit || mode === 'superseded'}
                                    onPick={onContentsLine} />
              )}
              {d.import_info && d.import_info.warnings.length > 0 && (
                <ul className="m-0 list-disc pl-5 text-[13px] text-paper-dim">
                  {d.import_info.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </div>
          )}
          {isEdit && d.edit_stale && <StaleEditBanner articleId={d.edits_article_id as number} path={section.path} />}
          {isEdit && editOpen && d.edit_twin_site && (
            <Banner tone="info" text={twinNotice(d.edit_twin_site)} />
          )}
          {isEdit && d.refresh && !d.edit_stale && (
            <>
              <WebChecks detail={d} open={['draft', 'needs_attention', 'approved'].includes(d.status)} onDetail={(r) => void took(r)} />
              {['draft', 'needs_attention', 'approved'].includes(d.status) && <LiveChanges id={d.id} />}
            </>
          )}
          {isEdit && d.status === 'rejected' && (
            <Banner tone="info" text="This edit was set aside. The live article stayed as it was; your words here are kept so you can copy from them." />
          )}
          {isEdit && mode === 'superseded' && (
            <Banner tone="info" text="Saved. These edits are on the live article." />
          )}
          {checking && <Banner tone="info" text={revising ? REVISE_NOTE : RECHECK_NOTE} />}
          {lineNotice && (
            <Banner tone="info" text={lineNotice}>
              <button type="button" className={btnSmall} onClick={() => setLineNotice(null)}>Dismiss</button>
            </Banner>
          )}
          {writerLock && d.status === 'queued' && <Banner tone="info" text={writerLock} />}
          {reviseFailed && <p role="alert" className="m-0 font-ui text-[13px] text-radar-red">The rewrite did not finish: {reviseFailed}</p>}
          {reloadError && <p role="alert" className="m-0 font-ui text-[13px] text-radar-red">{reloadError}</p>}
          {refreshFailed && <p role="status" className="m-0 font-ui text-[13px] text-paper-dim">Couldn't refresh — retrying</p>}
          {mode === 'check_sources' && (
            <Banner tone="info" text="This piece is waiting for you to check its sources.">
              <Link to={sourcesLink} className={btnPrimary}>Check the sources</Link>
            </Banner>
          )}
          {mode === 'failed' && (
            <Banner tone="bad" text={`This piece didn't finish: ${d.error ?? 'no reason was recorded'}`}>
              <Link to={tryAgainLink} className={btn}>Try again</Link>
            </Banner>
          )}
          {d.source_changed_at && (
            <Banner tone="info" text="The source changed since this version was made.">
              <span className="flex gap-2">
                <button type="button" className={btnSmall} onClick={() => setShowChanges((x) => !x)}>{showChanges ? 'Hide changes' : 'See changes'}</button>
                <button type="button" className={btnSmall} disabled={reviewed.isPending} onClick={() => reviewed.mutate()}>Mark reviewed</button>
              </span>
            </Banner>
          )}
          {reviewed.isError && <p role="alert" className="m-0 font-ui text-[13px] text-radar-red">{describeError(reviewed.error)}</p>}
          {showChanges && d.source_changed_at && <SourceDiff id={d.id} />}
          {mode === 'superseded' && !isEdit && (
            <Banner tone="info" text="This is an earlier version.">
              {d.superseded_by_id != null && <Link to={pieceHref(section.path, d.superseded_by_id)} className={link}>Open the newer version</Link>}
            </Banner>
          )}
          {d.status !== 'failed' && d.error && (
            <p role="alert" className="m-0 rounded-lg bg-radar-red/15 px-3.5 py-2.5 text-[13px] font-semibold text-radar-red">{d.error}</p>
          )}
          {mode !== 'check_sources' && mode !== 'failed' && (
            <>
              {isBoth && <PreviewSwitch view={shown} sectionLabel={section.label} onView={setView} />}
              <article className="relative rounded-md bg-ink-700 px-[clamp(20px,6vw,88px)] pb-14 pt-[clamp(24px,5vw,64px)] font-serif text-paper shadow-sm">
                {shown === 'public' && (
                  <p className="m-0 mb-[22px] rounded-lg bg-radar-cyan/10 px-3.5 py-2.5 font-ui text-[13px] font-semibold leading-snug text-radar-cyan">
                    {section.label} readers see this summary on the public site. It is written from the full piece, and you can change it here.
                  </p>
                )}
                <PhotoBlock detail={d} credit={local.featured_caption_override}
                            onCredit={(v) => setField('featured_caption_override', v)}
                            canEditCredit={editable} canChange={mode !== 'superseded' && !checking && !writerLock}
                            canPushCredit={canPublish(me.data)} onChanged={refetch}
                            onFraming={() => setFraming({ kind: 'featured' })}
                            beforeWrite={beforeWrite} onDetail={(r) => void took(r)} />
                {photoHelpOn && (
                  <PhotoHelpBox detail={d} beforeWrite={beforeWrite} onDetail={(r) => void took(r)} onChanged={refetch}
                                onBodyReplaced={(html) => editorRef.current?.setBody(html)} />
                )}
                <Headline title={local.title ?? d.title ?? ''} deck={local.meta_description ?? d.meta_description ?? ''} byline={local.byline ?? d.byline ?? ''}
                          subtitle={subtitle}
                          editable={editable} onTitle={(v) => setField('title', v)} onDeck={(v) => setField('meta_description', v)} onByline={(v) => setField('byline', v)} />
                {/* Kept mounted in the summary view so the editor keeps its text and undo history. */}
                <div hidden={shown !== 'members'}>
                  <EditorBoundary>
                  <Suspense fallback={<p role="status" className="m-0 font-ui text-[14px] text-paper-dim">Opening the editor…</p>}>
                  <DocumentEditor ref={editorRef} bodyHtml={d.body_html ?? ''} lockedPreviews={d.locked_previews} editable={editable && !claimBusy}
                                  onBody={(h) => { setSubtitle(leadingSubtitle(h)); if (writable === 'all') setBody(h) }} claims={claims} onRemoveClaim={onRemoveClaim} onConfirmClaim={onConfirmClaim}
                                  onTrimFigure={(assetId, pos) => setFraming({ kind: 'figure', assetId, pos })} />
                  </Suspense>
                  </EditorBoundary>
                  {brandsOn && d.brand && dest !== 'membership' && (
                    <p className="m-0 mt-6 border-t border-ink-500 pt-3 font-ui text-[13px] text-paper-faint">{closingLine(d.brand.name)}</p>
                  )}
                </div>
                {shown === 'public' && (
                  <SummaryEditor summary={local.public_summary ?? d.public_summary ?? ''} editable={summaryEditable}
                                 live={d.status === 'published'} onSave={onSaveSummary}
                                 draft={summaryDraft} onDraft={onSummaryDraft}
                                 canWrite={summaryEditable && !(d.status === 'published' && d.public_summary_edited)}
                                 sectionLabel={section.label}
                                 onChange={(v) => setField('public_summary', v)} onWrite={onWriteSummary} />
                )}
              </article>
              {framing && (
                <FramingDialog detail={d} target={framing} beforeWrite={beforeWrite}
                               onDetail={(r) => { void took(r); refetch() }}
                               onReplaceFigure={(old, a, pos) => editorRef.current?.replaceFigure(old, a, pos) ?? false}
                               onClose={() => setFraming(null)} />
              )}
            </>
          )}
        </div>
        <SidePanel detail={d} sectionLabel={section.label} publicationSlug={publicationSlug} onShowClaim={showClaim}
                   editable={mode === 'editable' && !writerLock && (!isEdit || editOpen)} locked={checking} local={local} onField={(f, v) => setField(f, v)}
                   slugFixed={isEdit} status={status} onRecheckStarted={statusChanged} onChanged={refetch} beforeWrite={beforeWrite} openTab={openTab}
                   path={section.path} onInsertFigure={(a) => editorRef.current?.insertFigure(a)} onDetail={(r) => void took(r)}
                   brandsOn={brandsOn && dest !== 'membership'} onBrand={onBrand} />
      </div>
    </div>
  )
}

/** The live article changed after this edit opened: nothing here can be saved. Starting again opens
 *  a fresh edit from the live version; this one stays readable to copy from. */
function StaleEditBanner({ articleId, path }: { articleId: number; path: string }) {
  const navigate = useNavigate()
  const restart = useMutation({
    mutationFn: () => restartEdit(articleId),
    onSuccess: (r) => navigate(pieceHref(path, r.draft_id)),
  })
  return (
    <Banner tone="bad" text="The live article changed since you began editing it, so this edit can't be saved. Start again from the live version; this copy stays here so you can copy your words from it.">
      <span className="flex flex-col items-end gap-1.5">
        <button type="button" className={btnPrimary} disabled={restart.isPending} onClick={() => restart.mutate()}>
          {restart.isPending ? 'Starting again…' : 'Start again from the live version'}
        </button>
        {restart.isError && <span role="alert" className="text-[13px] text-radar-red">{describeError(restart.error)}</span>}
      </span>
    </Banner>
  )
}

/** Which line of the issue's Contents this piece is: the viewer's Contents links that line to it. */
function ContentsLinePicker({ line, disabled, onPick }: {
  line: NonNullable<PieceDetail['contents_line']>; disabled: boolean; onPick: (tocId: number | null) => Promise<void>
}) {
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  if (line.lines.length === 0) {
    return <p className="m-0 text-[13px] text-paper-dim">{line.edition_title}: this issue has no Contents list yet.</p>
  }
  const pick = async (v: string) => {
    setError(null)
    setSaving(true)
    try {
      await onPick(v === '' ? null : Number(v))
    } catch (e) {
      setError(`Could not save the Contents line. ${describeError(e)}`)
    } finally {
      setSaving(false)
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-[13px] text-paper-dim">
      <label htmlFor="contents-line">In the {line.edition_title} Contents as</label>
      <select id="contents-line" value={line.toc_id ?? ''} disabled={disabled || saving}
              onChange={(e) => void pick(e.target.value)}
              className={`min-h-9 rounded-lg border border-ink-400 bg-ink-800 px-2 text-[13px] text-paper ${focus}`}>
        <option value="">Not in the Contents</option>
        {line.lines.map((t) => (
          <option key={t.id} value={t.id}>{t.page != null ? `p. ${t.page} · ` : ''}{t.title}</option>
        ))}
      </select>
      {saving && <span role="status">Saving…</span>}
      {error && <span role="alert" className="text-radar-red">{error}</span>}
    </div>
  )
}

function importDate(iso: string): string {
  const t = new Date(iso)
  return Number.isNaN(t.getTime()) ? iso : t.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'America/New_York' })
}

function SourceDiff({ id }: { id: number }) {
  const q = useQuery({ queryKey: ['source-changes', id], queryFn: () => sourceChanges(id) })
  if (q.isLoading) return <p role="status" className="m-0 font-ui text-[13px] text-paper-dim">Loading the changes…</p>
  if (q.isError || !q.data) return <p role="alert" className="m-0 font-ui text-[13px] text-radar-red">Could not load the changes. {describeError(q.error)}</p>
  return (
    <div>
      <p className="m-0 mb-1.5 font-ui text-[13px] font-semibold text-paper-dim">{changeSummaryText(changeSummary(q.data.ops))}</p>
      <div className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded-lg border border-ink-500 p-4 font-serif text-[16px] leading-relaxed text-paper">
        {q.data.ops.map((o, i) => o.op === 'insert' ? <ins key={i} className="bg-radar-green/20 no-underline">{o.text}</ins>
          : o.op === 'delete' ? <del key={i} className="bg-radar-red/15">{o.text}</del> : <span key={i}>{o.text}</span>)}
      </div>
    </div>
  )
}

function PreviewSwitch({ view, sectionLabel, onView }: { view: 'members' | 'public'; sectionLabel: string; onView: (v: 'members' | 'public') => void }) {
  const tabs: { key: 'members' | 'public'; label: string }[] = [
    { key: 'members', label: 'What members read' }, { key: 'public', label: `What ${sectionLabel} shows` },
  ]
  return (
    <div role="tablist" aria-label="Preview as" className="flex gap-0.5 self-center rounded-[10px] bg-ink-600 p-[3px] font-ui">
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={view === t.key} onClick={() => onView(t.key)}
                className={`min-h-9 rounded-lg px-3.5 text-[13px] ${focus} ${
                  view === t.key ? 'bg-ink-700 font-bold text-paper shadow-sm' : 'font-semibold text-paper-dim hover:text-paper'}`}>
          {t.label}
        </button>
      ))}
    </div>
  )
}

function Banner({ tone, text, children }: { tone: 'info' | 'bad'; text: string; children?: ReactNode }) {
  return (
    <div role="status" className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-[18px] py-4 font-ui ${
      tone === 'bad' ? 'border-radar-red/40 bg-radar-red/10' : 'border-ink-500 bg-ink-700'}`}>
      <p className={`m-0 flex-[1_1_260px] text-[15px] font-semibold ${tone === 'bad' ? 'text-radar-red' : 'text-paper'}`}>{text}</p>
      {children}
    </div>
  )
}
